<template>
    <div class="mt-10">
        <div class="input-box-holder" v-validate="errors" v-has-value="person.evidenceNumber">
          <TextField
            class="input-box"
            @input="setEvidenceNumber"
            :value="person.evidenceNumber"
            :id="`evidence-number_${index}`"
            required
          >
            <template v-slot:label :for="`evidence-number_${index}`">
              <label v-if="isID" class="tf-label">ID Card Number</label>
              <label v-else-if="isPassport" class="tf-label">Passport Number</label>
              <label v-else-if="isResidencePermit" class="tf-label">Permit Number</label>
              <label v-else-if="isZiadost" class="tf-label">Application Number</label>
            </template>
          </TextField>

          <div class="validation-output text-left mt-2">
            <ErrorHandler class="error" :id="`V029_${index}`">
              Enter document number.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V030_${index}`">
              Enter two letters and six digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V125_${index}`">
              Enter two letters and seven digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V126_${index}`">
              Enter two letters and seven digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V188_${index}`">
              Enter the residence permit number.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V187_${index}`">
              Enter the residence permit number in the correct format.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V259_${index}`">
              Enter the application number.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V260_${index}`">
              Enter the application number in the correct format.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V105_${index}`">
              <span v-if="!isZiadost">Enter the document number of the legal representative.</span>
              <span v-else>Enter the application number.</span>
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V308_${index}`">
              <span v-if="!isZiadost">Enter the document number of the legal representative.</span>
              <span v-else>Enter the application number.</span>
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V106_${index}`">
              Enter two letters and six digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V309_${index}`">
              Enter two letters and six digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V267_${index}`">
              Enter two letters and seven digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V312_${index}`">
              Enter two letters and seven digits.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V268_${index}`">
              Enter the application number in the correct format.
            </ErrorHandler>

            <ErrorHandler class="error" :id="`V313_${index}`">
              Enter the application number in the correct format.
            </ErrorHandler>
          </div>
        </div>

        <!-- Platnosti dokladu OD -->
        <div class="flex flex-col gap-2 color-gray-500 mt-15">
          <div class="input-box-holder" v-validate="errors" v-has-value="person.validityofDocumentFrom">
            <DateField
              v-if="isID || isPassport || isResidencePermit"
              class="input-box"
              v-model="person.validityOfDocumentFrom"
            >
              <p class="m-0 text-left weight-medium">
                Date Issued
              </p>
            </DateField>
            <DateField
              v-else
              class="input-box"
              v-model="person.validityOfZiadost"
            >
              <p class="m-0 text-left weight-medium">
                Date of Application
              </p>
            </DateField>
            <div class="validation-output text-left">
              <ErrorHandler class="error" :id="`V265_${index}`">
                Enter the date.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V261_${index}`">
                Enter the date.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V263_${index}`">
                The date of application cannot be later than today.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V264_${index}`">
                The date of submitting cannot be more than 60 days before the date of application.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V269_${index}`">
                Enter the date.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V270_${index}`">
                The date of application cannot be later than today.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V271_${index}`">
                The date of submitting cannot be more than 60 days before the date of application.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V314_${index}`">
                Enter the date.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V315_${index}`">
                The date of application cannot be later than today.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V316_${index}`">
                The date of submitting cannot be more than 60 days before the date of application.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V063_${index}`">
                Enter the date.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V311_${index}`">
                Enter the expiry date of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V284_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V286_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V319_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
            </div>
          </div>
        </div>

        <!-- Platnost dokladu DO -->
        <div v-if="!isZiadost" class="flex flex-col gap-2 color-gray-500 mt-5">
          <div class="input-box-holder" v-validate="errors" v-has-value="person.validityOfDocumentTo">
            <DateField
              class="input-box"
              v-model="person.validityOfDocumentTo"
              @blur="setValidityOfPermanentResidence"
            >
              <p v-if="isID || isPassport" class="m-0 text-left weight-medium">
                Date of Validity
              </p>
              <p v-else-if="isResidencePermit" class="m-0 text-left weight-medium">
                Date of Validity
              </p>
            </DateField>
            <div class="validation-output text-left">
              <ErrorHandler class="error" :id="`V266_${index}`">
                Enter the expiry date of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V064_${index}`">
                Enter the expiry date of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V311_${index}`">
                Enter the expiry date of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V285_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V287_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
              <ErrorHandler class="error" :id="`V320_${index}`">
                The identity document is invalid. Please verify that you have entered the correct expiry of the document.
              </ErrorHandler>
            </div>
          </div>
        </div>

        <!-- Platnost pobytu DO (v pripade Povolenia na pobyt)  -->
        <div
          v-if="person.evidenceType === enums.EvidenceTypeEnum.RESIDENCE_PERMIT"
          class="flex flex-col gap-2 color-gray-500 mt-5"
        >
          <div class="input-box-holder" v-validate="errors" v-has-value="person.validityOfPermanentResidence">
            <DateField
              class="input-box"
              v-model="person.validityOfPermanentResidence"
            >
              <p class="m-0 text-left weight-medium">
                Validity of Stay Until
              </p>
            </DateField>
            <div class="validation-output text-left mt-2">
              <ErrorHandler class="error" :id="`V190_${index}`">
                Enter the termination date of your stay.
              </ErrorHandler>

              <ErrorHandler class="error" :id="`V272_${index}`">
                Enter the date.
              </ErrorHandler>

              <ErrorHandler class="error" :id="`V317_${index}`">
                Enter the date.
              </ErrorHandler>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
import TextField from '@/components/TextField/TextField.vue';
import DateField from '@/components/DateField/DateField.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import enums from '@/mixins/enums';

export default {
  name: 'IdentityVerificationRadioContent',
  mixins: [enums],
  components: {
    DateField,
    TextField,
    ErrorHandler,
  },
  props: {
    person: { type: Object, required: false },
    errors: { type: Array, required: true },
    index: { type: Number, required: true },
  },
  computed: {
    isPassport() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.person.evidenceType === enumeration.PASSPORT;
    },
    isID() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.person.evidenceType === enumeration.ID;
    },
    isZiadost() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.person.evidenceType === enumeration.ZIADOST;
    },
    isResidencePermit() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.person.evidenceType === enumeration.RESIDENCE_PERMIT;
    },
  },
  methods: {
    // When all values in validityOfDocumentTo are set, fill validityOfPermanentResidence by them.
    // Otherwise validityOfPermanentResidence can be edited independently
    setValidityOfPermanentResidence() {
      const { validityOfPermanentResidence } = this.person;

      if (this.person.evidenceType === this.enums.EvidenceTypeEnum.RESIDENCE_PERMIT) {
        if (!validityOfPermanentResidence || ((!validityOfPermanentResidence.day && !validityOfPermanentResidence.month && !validityOfPermanentResidence.year))) {
          this.person.validityOfPermanentResidence = this.person.validityOfDocumentTo;
        }
      }
    },
    setEvidenceNumber(evidenceNumber) {
      this.person.evidenceNumber = evidenceNumber.replace(/\s+/g, '');
    },
  },
};
</script>
